import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Plus, MapPin, Briefcase, Clock } from 'lucide-react';

// Job type options
const jobTypeOptions = [
  { id: 'full-time', label: 'Full-time', icon: '💼' },
  { id: 'part-time', label: 'Part-time', icon: '⌛' },
  { id: 'contract', label: 'Contract', icon: '📝' },
  { id: 'freelance', label: 'Freelance', icon: '[ROCKET]' },
  { id: 'internship', label: 'Internship', icon: '🎓' },
  { id: 'remote', label: 'Remote', icon: '🏠' },
];

// Industry options
const industryOptions = [
  { id: 'tech', label: 'Technology' },
  { id: 'finance', label: 'Finance & Banking' },
  { id: 'healthcare', label: 'Healthcare' },
  { id: 'education', label: 'Education' },
  { id: 'retail', label: 'Retail' },
  { id: 'manufacturing', label: 'Manufacturing' },
  { id: 'media', label: 'Media & Entertainment' },
  { id: 'consulting', label: 'Consulting' },
  { id: 'nonprofit', label: 'Nonprofit' },
  { id: 'government', label: 'Government' },
  { id: 'hospitality', label: 'Hospitality & Tourism' },
  { id: 'construction', label: 'Construction' },
  { id: 'transportation', label: 'Transportation & Logistics' },
  { id: 'energy', label: 'Energy & Utilities' },
  { id: 'legal', label: 'Legal Services' },
  { id: 'agriculture', label: 'Agriculture' },
  { id: 'telecom', label: 'Telecommunications' },
  { id: 'pharma', label: 'Pharmaceuticals' },
  { id: 'automotive', label: 'Automotive' },
  { id: 'real-estate', label: 'Real Estate' },
];

// Salary range options
const salaryRangeOptions = [
  { id: 'not-specified', label: 'Prefer not to specify' },
  { id: 'below-5', label: 'Below ₹5 LPA' },
  { id: '5-10', label: '₹5-10 LPA' },
  { id: '10-15', label: '₹10-15 LPA' },
  { id: '15-25', label: '₹15-25 LPA' },
  { id: '25-50', label: '₹25-50 LPA' },
  { id: 'above-50', label: 'Above ₹50 LPA' },
];

// Popular cities in India
const popularCities = [
  'Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai', 'Kolkata', 
  'Pune', 'Ahmedabad', 'Jaipur', 'Surat', 'Lucknow', 'Kanpur', 
  'Nagpur', 'Indore', 'Thane', 'Bhopal', 'Visakhapatnam', 'Vadodara',
  'Ghaziabad', 'Ludhiana', 'Agra', 'Nashik', 'Faridabad', 'Meerut',
  'Rajkot', 'Kalyan', 'Vasai', 'Varanasi', 'Srinagar', 'Aurangabad'
];

const PreferencesStep = ({ formData, updateFormData, onNext, onPrev, onSkip }) => {
  // Initialize state with existing data or defaults
  const [jobTypes, setJobTypes] = useState(formData.preferences?.jobTypes || []);
  const [industries, setIndustries] = useState(formData.preferences?.industries || []);
  const [salaryRange, setSalaryRange] = useState(formData.preferences?.salaryRange || '');
  const [locations, setLocations] = useState(formData.preferences?.locations || []);
  const [newLocation, setNewLocation] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [workExperience, setWorkExperience] = useState(formData.preferences?.workExperience || '');
  const [jobTitle, setJobTitle] = useState(formData.preferences?.jobTitle || '');
  
  // Check if any preferences are from parsed resume
  const [hasParsedData, setHasParsedData] = useState(false);
  
  useEffect(() => {
    // Check if we have parsed resume data
    if (formData.resume && formData.resume.parsed && formData.resume.parsedData?.preferences) {
      const parsedPrefs = formData.resume.parsedData.preferences;
      
      // Only set if we don't already have preferences
      if (!jobTypes.length && !industries.length && !locations.length) {
        if (parsedPrefs.jobTypes) setJobTypes(parsedPrefs.jobTypes);
        if (parsedPrefs.industries) setIndustries(parsedPrefs.industries);
        if (parsedPrefs.locations) setLocations(parsedPrefs.locations);
        if (parsedPrefs.jobTitle) setJobTitle(parsedPrefs.jobTitle);
        if (parsedPrefs.salaryRange) setSalaryRange(parsedPrefs.salaryRange);
        if (parsedPrefs.workExperience) setWorkExperience(parsedPrefs.workExperience);
        
        setHasParsedData(true);
      }
    }
  }, [formData.resume, jobTypes.length, industries.length, locations.length]);
  
  // Handle job type selection
  const toggleJobType = (jobTypeId) => {
    setJobTypes(prev => {
      if (prev.includes(jobTypeId)) {
        return prev.filter(id => id !== jobTypeId);
      } else {
        return [...prev, jobTypeId];
      }
    });
    setHasParsedData(false);
  };
  
  // Handle industry selection
  const toggleIndustry = (industryId) => {
    setIndustries(prev => {
      if (prev.includes(industryId)) {
        return prev.filter(id => id !== industryId);
      } else {
        return [...prev, industryId];
      }
    });
    setHasParsedData(false);
  };
  
  // Handle location input
  const handleLocationInput = (e) => {
    const value = e.target.value;
    setNewLocation(value);
    
    if (value.trim()) {
      // Filter suggestions based on input
      const filtered = popularCities.filter(city => 
        city.toLowerCase().includes(value.toLowerCase())
      );

      setLocationSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setLocationSuggestions([]);
      setShowSuggestions(false);
    }
  };
  
  // Add location
  const addLocation = (location = null) => {
    const locationToAdd = location || newLocation.trim();
    
    if (locationToAdd && !locations.includes(locationToAdd)) {
      setLocations(prev => [...prev, locationToAdd]);
      setNewLocation('');
      setLocationSuggestions([]);
      setShowSuggestions(false);
      setHasParsedData(false);
    }
  };
  
  // Remove location
  const removeLocation = (location) => {
    setLocations(prev => prev.filter(loc => loc !== location));
    setHasParsedData(false);
  };
  
  // Handle continue
  const handleContinue = () => {
    updateFormData('preferences', {
      jobTypes,
      industries,
      salaryRange,
      locations,
      workExperience,
      jobTitle
    });
    onNext();
  };
  
  // Get job type icon
  const getJobTypeIcon = (id) => {
    const jobType = jobTypeOptions.find(option => option.id === id);
    return jobType ? jobType.icon : '💼';
  };
  
  // Get job type label
  const getJobTypeLabel = (id) => {
    const jobType = jobTypeOptions.find(option => option.id === id);
    return jobType ? jobType.label : id;
  };
  
  // Get industry label
  const getIndustryLabel = (id) => {
    const industry = industryOptions.find(option => option.id === id);
    return industry ? industry.label : id;
  };

  return (
    <div className="max-w-2xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-3xl font-bold text-center mb-2">Your Job Preferences 🎯</h1>
        <p className="text-gray-600 text-center mb-8">
          Tell us what you're looking for in your next role
        </p>
        
        {hasParsedData && (
          <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-8">
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-blue-700">We've pre-filled some preferences based on your resume. Feel free to edit them.</p>
            </div>
          </div>
        )}
        
        <div className="space-y-8">
          {/* Desired Job Title */}
          <div>
            <label htmlFor="jobTitle" className="block text-lg font-medium text-gray-700 mb-2">
              Desired Job Title
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Briefcase size={18} className="text-gray-400" />
              </div>
              <input
                type="text"
                id="jobTitle"
                value={jobTitle}
                onChange={(e) => {
                  setJobTitle(e.target.value);
                  setHasParsedData(false);
                }}
                className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                placeholder="e.g. Software Engineer, Product Manager"
              />
            </div>
          </div>
          
          {/* Job Types */}
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-2">
              Job Types
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {jobTypeOptions.map((option) => (
                <div
                  key={option.id}
                  onClick={() => toggleJobType(option.id)}
                  className={`flex items-center p-3 border rounded-md cursor-pointer transition-colors ${
                    jobTypes.includes(option.id)
                      ? 'border-yellow-400 bg-yellow-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-xl mr-2">{option.icon}</span>
                  <span className="text-gray-800">{option.label}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Industries */}
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-2">
              Preferred Industries
            </label>
            <div className="flex flex-wrap gap-2">
              {industries.map((industryId) => (
                <div
                  key={industryId}
                  className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full flex items-center"
                >
                  <span>{getIndustryLabel(industryId)}</span>
                  <button
                    type="button"
                    onClick={() => toggleIndustry(industryId)}
                    className="ml-2 text-gray-500 hover:text-gray-700"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
            
            <div className="mt-3">
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                value=""
                onChange={(e) => {
                  if (e.target.value) {
                    toggleIndustry(e.target.value);
                  }
                }}
              >
                <option value="">Select industries...</option>
                {industryOptions
                  .map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
              </select>
            </div>
          </div>
          
          {/* Locations */}
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-2">
              Preferred Locations
            </label>
            <div className="flex flex-wrap gap-2 mb-3">
              {locations.map((location) => (
                <div
                  key={location}
                  className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full flex items-center"
                >
                  <MapPin size={14} className="mr-1" />
                  <span>{location}</span>
                  <button
                    type="button"
                    onClick={() => removeLocation(location)}
                    className="ml-2 text-gray-500 hover:text-gray-700"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
            
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MapPin size={18} className="text-gray-400" />
              </div>
              <input
                type="text"
                value={newLocation}
                onChange={handleLocationInput}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addLocation();
                  }
                }}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                placeholder="Add a location..."
              />
              {newLocation && (
                <button
                  type="button"
                  onClick={() => addLocation()}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                >
                  <Plus size={18} />
                </button>
              )}
            </div>
            
            {showSuggestions && locationSuggestions.length > 0 && (
              <div className="mt-1 bg-white border border-gray-200 rounded-md shadow-sm max-h-48 overflow-y-auto">
                {locationSuggestions.map((city) => (
                  <div
                    key={city}
                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                    onClick={() => addLocation(city)}
                  >
                    {city}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Salary Range */}
          <div>
            <label htmlFor="salaryRange" className="block text-lg font-medium text-gray-700 mb-2">
              Expected Salary Range
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <DollarSign size={18} className="text-gray-400" />
              </div>
              <select
                id="salaryRange"
                value={salaryRange}
                onChange={(e) => {
                  setSalaryRange(e.target.value);
                  setHasParsedData(false);
                }}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
              >
                <option value="">Select salary range...</option>
                {salaryRangeOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Work Experience */}
          <div>
            <label htmlFor="workExperience" className="block text-lg font-medium text-gray-700 mb-2">
              Total Work Experience
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Clock size={18} className="text-gray-400" />
              </div>
              <select
                id="workExperience"
                value={workExperience}
                onChange={(e) => {
                  setWorkExperience(e.target.value);
                  setHasParsedData(false);
                }}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
              >
                <option value="">Select experience level...</option>
                <option value="fresher">Fresher (0-1 years)</option>
                <option value="junior">Junior (1-3 years)</option>
                <option value="mid">Mid-level (3-5 years)</option>
                <option value="senior">Senior (5-10 years)</option>
                <option value="expert">Expert (10+ years)</option>
              </select>
            </div>
          </div>
        </div>
        
        {/* Summary */}
        {(jobTypes.length > 0 || industries.length > 0 || locations.length > 0 || salaryRange || workExperience || jobTitle) && (
          <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-800 mb-3">Your Preferences Summary</h3>
            
            <div className="space-y-3">
              {jobTitle && (
                <div className="flex items-start">
                  <Briefcase size={16} className="text-gray-500 mr-2 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Desired Role</p>
                    <p className="text-gray-600">{jobTitle}</p>
                  </div>
                </div>
              )}
              
              {jobTypes.length > 0 && (
                <div className="flex items-start">
                  <Building size={16} className="text-gray-500 mr-2 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Job Types</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {jobTypes.map(type => (
                        <span key={type} className="inline-flex items-center text-sm bg-gray-100 px-2 py-1 rounded">
                          <span className="mr-1">{getJobTypeIcon(type)}</span>
                          {getJobTypeLabel(type)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {industries.length > 0 && (
                <div className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500 mr-2 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Industries</p>
                    <p className="text-gray-600">{industries.map(id => getIndustryLabel(id)).join(', ')}</p>
                  </div>
                </div>
              )}
              
              {locations.length > 0 && (
                <div className="flex items-start">
                  <MapPin size={16} className="text-gray-500 mr-2 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Locations</p>
                    <p className="text-gray-600">{locations.join(', ')}</p>
                  </div>
                </div>
              )}
              
              {salaryRange && (
                <div className="flex items-start">
                  <DollarSign size={16} className="text-gray-500 mr-2 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Expected Salary</p>
                    <p className="text-gray-600">
                      {salaryRangeOptions.find(option => option.id === salaryRange)?.label || salaryRange}
                    </p>
                  </div>
                </div>
              )}
              
              {workExperience && (
                <div className="flex items-start">
                  <Clock size={16} className="text-gray-500 mr-2 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Experience Level</p>
                    <p className="text-gray-600">
                      {{
                        'fresher': 'Fresher (0-1 years)',
                        'junior': 'Junior (1-3 years)',
                        'mid': 'Mid-level (3-5 years)',
                        'senior': 'Senior (5-10 years)',
                        'expert': 'Expert (10+ years)'
                      }[workExperience] || workExperience}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Navigation buttons */}
        <div className="flex justify-between mt-8">
          <button
            type="button"
            className="px-6 py-2 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors"
            onClick={onPrev}
          >
            Back
          </button>
          
          <div className="flex gap-4">
            <button
              type="button"
              className="px-6 py-2 text-gray-500 font-medium hover:text-gray-700 transition-colors"
              onClick={() => {
                updateFormData('preferences', {
                  jobTypes,
                  industries,
                  salaryRange,
                  locations,
                  workExperience,
                  jobTitle
                });
                onSkip();
              }}
            >
              Skip for now
            </button>
            
            <motion.button
              type="button"
              className="px-8 py-3 bg-black text-white font-bold rounded-md shadow-md hover:bg-gray-800 transition-all"
              onClick={handleContinue}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Continue
            </motion.button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PreferencesStep;
